import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay, PercentDisplay } from '@/components/ui/stat-card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import { format, differenceInDays, eachDayOfInterval, eachWeekOfInterval, startOfWeek } from 'date-fns';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

const formatCompact = (amount: number) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
};

interface ContractsAnalyticsProps {
    monthRange: { from: Date; to: Date };
}


export default function ContractsAnalytics({ monthRange }: ContractsAnalyticsProps) {
    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const periodDays = differenceInDays(monthRange.to, monthRange.from) + 1;

    // Fetch current period analytics using RPC
    const { data: currentData, isLoading: currentLoading } = useQuery({
        queryKey: ['contracts-analytics-current', fromDate, toDate],
        queryFn: async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data: rpcData, error: rpcError } = await (supabase as any).rpc('get_contracts_analytics', {
                from_date: fromDate,
                to_date: toDate,
            });

            if (rpcError) throw rpcError;

            const analytics = rpcData as {
                totalContracts: number;
                vehicleSalesTotal: number;
                insuranceLoansTotal: number;
                loansTotal: number;
                overdueInterestTotal: number;
                totalValue: number;
                avgContractValue: number;
                activeContracts: number;
                completedContracts: number;
                terminatedContracts: number;
                pendingContracts: number;
                uniqueDrivers: number;
                uniqueVehicles: number;
            };

            // Calculate derived rates
            const completionRate = analytics.totalContracts > 0
                ? (analytics.completedContracts / analytics.totalContracts) * 100
                : 0;
            const terminationRate = analytics.totalContracts > 0
                ? (analytics.terminatedContracts / analytics.totalContracts) * 100
                : 0;

            // Fetch contracts data for other charts (status distribution, heatmap, etc.)
            // Get signed_at from contract_agreements where type = 'DRIVER'
            const { data: contracts } = await supabase
                .from('contracts')
                .select(`
                    id, 
                    sold_at, 
                    contract_updates(status, created_at),
                    contract_agreements!inner(signed_at, type)
                `)
                .eq('contract_agreements.type', 'DRIVER')
                .gte('contract_agreements.signed_at', fromDate)
                .lte('contract_agreements.signed_at', toDate + 'T23:59:59')
                .order('created_at', { ascending: true });

            // Map to include signed_at at top level for compatibility
            const contractsWithSignedAt = (contracts || []).map((c: any) => ({
                ...c,
                signed_at: c.contract_agreements?.[0]?.signed_at || null,
            }));

            return {
                ...analytics,
                completionRate,
                terminationRate,
                contracts: contractsWithSignedAt,
            };
        },
    });



    // Monthly chart data from RPC (includes full value calculation)
    const { data: monthlyChartData = [] } = useQuery({
        queryKey: ['contracts-monthly-trend', fromDate, toDate],
        queryFn: async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc('get_contracts_monthly_trend', {
                from_date: fromDate,
                to_date: toDate,
            });
            if (error) throw error;
            return data || [];
        },
    });

    // Status distribution data for donut chart
    const statusDistribution = useMemo(() => {
        if (!currentData) return [];
        return [
            { name: 'Active', value: currentData.activeContracts, color: '#22c55e' },
            { name: 'Completed', value: currentData.completedContracts, color: '#3b82f6' },
            { name: 'Terminated', value: currentData.terminatedContracts, color: '#ef4444' },
            { name: 'Draft', value: currentData.pendingContracts, color: '#f59e0b' },
        ].filter(d => d.value > 0);
    }, [currentData]);

    // Value breakdown data for donut chart
    const valueBreakdown = useMemo(() => {
        if (!currentData) return [];
        return [
            { name: 'Vehicle Sales', value: currentData.vehicleSalesTotal || 0, color: '#22c55e' },
            // NOT "Premium Advances" ANY MORE [D-B46 R8/R9]. `insuranceLoansTotal` is
            // `A + (A − D)·r/100` — the ledger's own insurance contribution to contract value — so
            // the slice carries a paid-in-full year's whole premium as value alongside the
            // advances, and the interest in it is on the amount advanced. It is the estate's
            // premiums with their interest, not its advances.
            { name: 'Insurance premiums (with interest)', value: currentData.insuranceLoansTotal || 0, color: '#3b82f6' },
            { name: 'Expense Advances', value: currentData.loansTotal || 0, color: '#8b5cf6' },
            { name: 'Overdue Interest', value: currentData.overdueInterestTotal || 0, color: '#f59e0b' },
        ].filter(d => d.value > 0);
    }, [currentData]);



    // Daily signing data for calendar heatmap
    const dailySigningData = useMemo(() => {
        if (!currentData?.contracts) return [];

        const dailyMap = new Map<string, number>();

        // Initialize all days in range
        const days = eachDayOfInterval({ start: monthRange.from, end: monthRange.to });
        days.forEach(d => {
            dailyMap.set(format(d, 'yyyy-MM-dd'), 0);
        });

        // Count signings per day
        currentData.contracts.forEach((c: any) => {
            if (!c.signed_at) return;
            const dateKey = c.signed_at.substring(0, 10);
            if (dailyMap.has(dateKey)) {
                dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + 1);
            }
        });

        return Array.from(dailyMap.entries()).map(([date, count]) => ({
            date,
            count,
        }));
    }, [currentData, monthRange]);

    if (currentLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-5">
                    {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Row 1: 5 KPI Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Contracts"
                    value={currentData?.totalContracts || 0}
                    description="Total number of contracts signed in the selected period"
                />
                <StatCard
                    title="Total Value"
                    value={<CurrencyDisplay amount={currentData?.totalValue || 0} />}
                    description="Total value including vehicle sales, advances (with interest), and overdue charges."
                />
                <StatCard
                    title="Average Value"
                    value={<CurrencyDisplay amount={currentData?.avgContractValue || 0} />}
                    description="Mean contract value including all components"
                />
                <StatCard
                    title="Draft"
                    value={currentData?.pendingContracts || 0}
                    description="Contracts awaiting activation"
                />
                <StatCard
                    title="Terminated"
                    value={currentData?.terminatedContracts || 0}
                    description="Contracts that were cancelled or ended early"
                />
            </div>

            {/* Row 2: 5 KPI Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Active Contracts"
                    value={currentData?.activeContracts || 0}
                    description="Contracts currently in progress"
                />
                <StatCard
                    title="Completion Rate"
                    value={<PercentDisplay value={currentData?.completionRate || 0} />}
                    description="Percentage of contracts that reached COMPLETED status"
                />
                <StatCard
                    title="Termination Rate"
                    value={<PercentDisplay value={currentData?.terminationRate || 0} />}
                    description="Percentage of contracts that were terminated"
                />
                <StatCard
                    title="Unique Drivers"
                    value={currentData?.uniqueDrivers || 0}
                    description="Distinct drivers with contracts in the period"
                />
                <StatCard
                    title="Unique Vehicles"
                    value={currentData?.uniqueVehicles || 0}
                    description="Distinct vehicles under contract in the period"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Monthly Contract Trend */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Monthly Contract Trend</CardTitle>
                        <CardDescription>Contracts signed and value per month</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={monthlyChartData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" className="text-xs" />
                                <YAxis className="text-xs" yAxisId="left" />
                                <YAxis className="text-xs" yAxisId="right" orientation="right" tickFormatter={formatCompact} />
                                <RechartsTooltip
                                    formatter={(value: number, name: string) => {
                                        if (name === 'Contracts') {
                                            return [value, 'Contracts'];
                                        }
                                        return [formatCurrency(Number(value.toFixed(2))), 'Total Value'];
                                    }}
                                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                                />
                                <Legend />
                                <Area type="monotone" dataKey="contracts" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} name="Contracts" yAxisId="left" />
                                <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} name="Value" yAxisId="right" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Contract Status Distribution */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Status Distribution</CardTitle>
                        <CardDescription>Breakdown by current contract status</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={statusDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                    {statusDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row 1.5: Value Breakdown and Signing Frequency */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Value Breakdown */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Value Breakdown</CardTitle>
                        <CardDescription>Total contract value by category</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={valueBreakdown} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis type="number" tickFormatter={formatCompact} className="text-xs" />
                                <YAxis type="category" dataKey="name" className="text-xs" width={110} />
                                <RechartsTooltip
                                    formatter={(value: number) => formatCurrency(value)}
                                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                                />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                    {valueBreakdown.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Contract Signing Frequency Calendar Heatmap */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Signing Frequency</CardTitle>
                        <CardDescription>Daily contract signing activity</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto" style={{ width: '100%', maxWidth: `${Math.ceil(periodDays / 7) * 45 + 50}px` }}>
                            <CalendarHeatmap
                                startDate={monthRange.from}
                                endDate={monthRange.to}
                                values={dailySigningData}
                                classForValue={(value) => {
                                    if (!value || value.count === 0) return 'fill-muted';
                                    if (value.count >= 5) return 'fill-green-700';
                                    if (value.count >= 3) return 'fill-green-500';
                                    if (value.count >= 2) return 'fill-green-400';
                                    return 'fill-green-300';
                                }}
                                titleForValue={(value) => {
                                    if (!value || !value.date) return '';
                                    return `${value.date}: ${value.count} contract${value.count !== 1 ? 's' : ''}`;
                                }}
                                showWeekdayLabels
                                gutterSize={2}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-4 text-xs text-muted-foreground">
                            <span>Less</span>
                            <div className="w-3 h-3 rounded-sm bg-muted"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-300"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-400"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-700"></div>
                            <span>More</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
